<template>
	<BaseLoading
		v-if="isLoading"
	/>
	<CForm
		v-else
		class="main-wrapper form-standard"
	>
		<CRow>
			<CCol lg="12">
				<CInput
					v-model="form.email"
					:label="`${$t('message.email_address')}*`"
					:is-valid="formState.email.valid"
					:invalid-feedback="formState.email.error"
					:disabled="formDisabled.email || isUser"
				/>
			</CCol>
		</CRow>
		<CRow>
			<CCol lg="12">
				<template>
					<CInput
						v-if="showChangePassword"
						v-model="$v.form.password.$model"
						:label="`${$t('message.password')}*`"
						:is-valid="formState.password.valid"
						:invalid-feedback="formState.password.error"
						:disabled="isUser"
						type="password"
						autocomplete="new-password"
					/>
					<div
						v-else
						class="form-group"
					>
						<label>Password</label>
						<div class="d-block">
							<CButton
								v-if="isAdmin"
								class="btn-change-password flex transparent btn-export-csv"
								color="tertiary"
								@click="showChangePassword = true"
							>
								Change password
							</CButton>
						</div>
					</div>
				</template>
			</CCol>
		</CRow>
		<CRow>
			<CCol lg="6">
				<CInput
					v-model="$v.form.firstName.$model"
					:label="`${$t('message.first_name')}*`"
					:is-valid="formState.firstName.valid"
					:invalid-feedback="formState.firstName.error"
					:disabled="isUser"
				/>
			</CCol>
			<CCol lg="6">
				<CInput
					v-model="form.lastName"
					:label="$t('message.last_name')"
					:description="$t('message.optional')"
					:disabled="isUser"
				/>
			</CCol>
		</CRow>
		<CRow>
			<CCol lg="12">
				<BaseDropDown
					v-model="form.role"
					:options="roleOptions"
					:label-drop-down="$t('message.user_role')"
					:placeholder="$t('message.selection')"
					:searchable="formDisabled.role"
					:disabled="isUser"
					label="name"
					track-by="value"
				/>
			</CCol>
		</CRow>
		<CRow>
			<CCol lg="12" class="mt-4 mb-4">
				<BaseSwitch
					v-model="form.status"
					:label="$t('message.status')"
					:checked-text="$t('message.active')"
					:unchecked-text="$t('message.inactive')"
					:disabled="isUser"
				/>
			</CCol>
		</CRow>
		<CRow>
			<CCol lg="12">
				<CInput
					v-model="form.phoneNumber"
					:label="$t('message.phone_number')"
					:description="$t('message.optional')"
					:disabled="isUser"
				/>
			</CCol>
		</CRow>
		<CRow class="container-date">
			<CCol lg="12">
				<p>
					{{ $t('message.date_registered', { date: createdAt }) }}
				</p>
				<p>
					{{ $t('message.date_modified', { date: updatedAt }) }}
				</p>
			</CCol>
		</CRow>
		<BaseActionSubmit
			v-if="isAdmin"
			:is-loading="isSaving"
			:submit-text="$t('button.save_change')"
			@onSubmit="handleSubmit"
		/>
	</CForm>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { inputStateValid, inputStateError, formatPassword } from '../helpers/vuelidator';
import { USER_ROLE } from '../enums/user';

export default {
	name: 'UserCreate',

	data() {
		return {
			form: {
				email: null,
				password: null,
				firstName: null,
				lastName: null,
				status: true,
				phoneNumber: null,
				role: {
					name: null,
					value: null,
				},
			},
			roleOptions: [
				{
					name: 'Admin',
					value: USER_ROLE.ADMIN,
				},
				{
					name: 'User',
					value: USER_ROLE.USER,
				},
			],
			showChangePassword: false,
		};
	},

	validations: {
		form: {
			password: {
				required(value) {
					return this.showChangePassword
						? required(value)
						: true;
				},
				formatPassword,
			},
			firstName: {
				required,
			},
		},
	},

	computed: {
		...mapState('userSingle', {
			data: 'data',
			isLoading: 'isLoading',
		}),
		...mapState('userUpdate', {
			error: 'error',
			isSaving: 'isLoading',
		}),
		...mapGetters('userSingle', {
			createdAt: 'createdAt',
			updatedAt: 'updatedAt',
		}),
		...mapGetters('me', {
			isUser: 'isUser',
			isAdmin: 'isAdmin',
		}),
		formState() {
			return {
				email: {
					valid: inputStateValid(this.$v.form.email),
					error: inputStateError(this.$v.form.email)(this),
				},
				password: {
					valid: inputStateValid(this.$v.form.password),
					error: inputStateError(this.$v.form.password)(this),
				},
				firstName: {
					valid: inputStateValid(this.$v.form.firstName),
					error: inputStateError(this.$v.form.firstName)(this),
				},
			};
		},
		formDisabled() {
			return {
				// email always disabled
				email: true,
			};
		},
	},

	async created() {
		await this.getUser(this.$route.params.id);

		// set state, form
		this.setState();

		// set page title
		this.setPageTitle({
			title: this.data.name,
			isShow: true,
		});
	},

	methods: {
		...mapActions('pageTitle', {
			setPageTitle: 'setPageTitle',
		}),
		...mapActions('toast', {
			showToast: 'showToast',
		}),
		...mapActions('userSingle', {
			getUser: 'getUser',
		}),
		...mapActions('userUpdate', {
			updateUser: 'updateUser',
		}),
		async handleSubmit() {
			this.$v.$touch();

			const isValidForm = !this.$v.$invalid;

			if (isValidForm) {
				await this.updateUser({
					id: this.$route.params.id,
					params: {
						first_name: this.form.firstName,
						last_name: this.form.lastName,
						phone_number: this.form.phoneNumber,
						role: this.form.role?.value,
						is_active: this.form.status,
						// if user change password, set password in the params
						...(
							this.showChangePassword
								? { password: this.form.password }
								: null
						),
					},
				});

				if (this.error === null) {
					// show toast with success
					this.showToast({
						header: this.$t('toast.success'),
						content: this.$t('toast.updated_user'),
						type: 'success',
					});
					// reset state for this component
					this.resetState();
					// refresh user info, request to api to get user again
					await this.getUser(this.$route.params.id);
					// set state, form
					this.setState();
					// set page title
					this.setPageTitle({
						title: this.data.name,
						isShow: true,
					});
				} else {
					// show toast with error
					this.showToast({
						header: this.$t('toast.unable_to_save'),
						content: this.error.message,
						type: 'danger',
					});
				}
			}
		},
		// side effect function, reset form
		resetState() {
			this.showChangePassword = false;
			this.form.email = null;
			this.form.password = null;
			this.form.firstName = null;
			this.form.lastName = null;
			this.form.status = null;
			this.form.phoneNumber = null;
			this.form.role = {};
		},
		// side effect function, set form
		setState() {
			this.showChangePassword = false;
			this.form.email = this.data.email;
			this.form.password = this.data.password;
			this.form.firstName = this.data.first_name;
			this.form.lastName = this.data.last_name;
			this.form.status = !!this.data.is_active;
			this.form.phoneNumber = this.data.phone_number;
			this.form.role = {
				name: this.roleOptions.find((item) => item.value === this.data.role).name,
				role: this.data.role,
			};
		},
	},
};
</script>

<style lang="scss" scoped>
.container-date {
	@include typo-helper-text;

	color: $color-black-45;

	p {
		margin-bottom: rem(4);

		&:last-of-type {
			margin-bottom: 0;
		}
	}
}
</style>